import { useNavigate } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import SeoHead from '../../components/feature/SeoHead';

const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://www.cherokeeyouthwarriors.com';

const eventsSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Events & Activities | Cherokee Youth Warriors',
  description: 'Upcoming events, fundraisers, cheer camps, summer camps, and skills clinics for Cherokee Youth Football & Cheer in Canton, GA.',
  url: `${siteUrl}/events`,
  isPartOf: { '@type': 'WebSite', url: siteUrl, name: 'Cherokee Youth Warriors' },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Events', item: `${siteUrl}/events` },
    ],
  },
};

export default function EventsPage() {
  const navigate = useNavigate();

  const generalSections = [
    {
      id: 'skills-clinics',
      title: 'SKILLS CLINICS',
      icon: 'ri-trophy-line',
      description: 'Sharpen your game with specialized training sessions led by experienced coaches. Our clinics focus on technique, fundamentals, and position-specific skills for both football and cheer.',
      image: 'https://public.readdy.ai/ai/img_res/c1b9be6cb59a384e7c9303f78645e6eb.jpg',
      comingSoon: true
    },
  ];

  const fundraisingEvents = [
    {
      id: 'chick-fil-a',
      title: 'Chick-fil-A Spirit Night',
      date: 'Thursday, March 19th, 2026',
      time: '4:00 PM \u2013 8:00 PM',
      location: 'Chick-fil-A Riverstone, 1459 Riverstone Pkwy, Canton, GA',
      details: 'Show your Cherokee Warriors spirit! A portion of all purchases made during this event will go directly back to our program. Use Mobile App Code: Cherokee (available 7am\u20138pm via Chick-fil-A Mobile App).',
      graphic: 'https://static.readdy.ai/image/ad8804c8cf5327b346b1700de5ce7056/e323340cf84afe06fe0581ac84dbcffa.png',
    },
    {
      id: 'your-pie',
      title: 'Your Pie Spirit Night',
      date: 'Tuesday, April 22nd, 2026',
      time: '11:00 AM \u2013 9:00 PM',
      location: 'Your Pie Canton, 2247 Cumming Hwy, Canton, GA 30115',
      details: 'Bring your family and friends out to raise money for your Cherokee Youth Football and Cheer program! A percentage of total sales from the day \u2014 including deliveries and curbside orders \u2014 will be donated directly back to the teams.',
      graphic: 'https://static.readdy.ai/image/ad8804c8cf5327b346b1700de5ce7056/c80756392826d598322b47367b8d7b76.jpeg',
    },
  ];

  const cheerCamps = [
    {
      id: 'coach-holley-football-camp',
      title: "Coach Holley's Youth Football Camp",
      date: 'June 1–3, 2026',
      time: 'Times TBA',
      location: 'Cherokee High School',
      ages: null,
      cost: 'TBA',
      details: "Mark your calendars for our varsity head coach's annual youth football camp, June 1–3! More details via the link below!",
      graphic: 'https://static.readdy.ai/image/ad8804c8cf5327b346b1700de5ce7056/37f69b3d271c733d1db5fa5ea04caab2.jpeg',
      registerLink: 'https://www.cherokeewarriorsfootball.org/product/2026-cherokee-warrior-youth-football-camp/PGHO72GFBPL4PVW3TWIAN2DH?cp=true&sa=true&sbp=false&q=false',
    },
    {
      id: 'lil-warrior-cheer-camp',
      title: "Lil' Warrior Cheer Camp",
      date: 'June 15\u201317, 2026',
      time: '9:00 AM \u2013 12:00 PM',
      location: 'CHS North Gym',
      ages: 'Pre K \u2013 5th Grade',
      cost: '$80 (includes shirt & bow)',
      details: "Join us for the Lil' Warrior Cheer Camp! Young cheerleaders from Pre K through 5th grade are invited to learn cheers, stunts, and team spirit in a fun and supportive environment. Cost is $80 and includes a shirt and bow.",
      graphic: 'https://static.readdy.ai/image/ad8804c8cf5327b346b1700de5ce7056/55667cb5c0dd0439ae22a9af8281e67c.jpeg',
      registerLink: 'https://chscompcheer.square.site/product/lil-warrior-cheer-camp-2026/FNXOBJAMGVTPVD24RNKDY5DK',
    },
    {
      id: 'all-sports-camp',
      title: '2026 All Sports Camp',
      date: 'June 22\u201326, 2026',
      time: '9:00 AM \u2013 3:00 PM Daily',
      location: '\u201cOld\u201d Cherokee High School',
      ages: null,
      cost: '$240.00',
      details: 'In partnership with varsity Head Basketball Coach Joe Veihman, the 2026 All Sports Camp offers a full week of multi-sport fun and skill development at the \u201cOld\u201d Cherokee High School. A great opportunity for young athletes to stay active and grow this summer!',
      graphic: 'https://static.readdy.ai/image/ad8804c8cf5327b346b1700de5ce7056/d344b152196ca0c888266ae815af0808.jpeg',
      registerLink: 'https://bwfcamps.square.site/2026-summer-camps',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title="Events & Activities | Cherokee Youth Warriors Canton GA"
        description="Upcoming fundraisers, cheer camps, summer camps, and skills clinics for Cherokee Youth Football & Cheer in Canton, GA. Stay connected with the Warriors community."
        keywords="Cherokee Warriors events, youth football camps Canton GA, cheer camp, fundraiser, skills clinic"
        canonical={`${siteUrl}/events`}
        schemaJson={eventsSchema}
      />
      <Header />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-warrior-black via-gray-900 to-warrior-black py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        <div className="absolute top-10 right-10 w-64 h-64 bg-warrior-red/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 left-10 w-80 h-80 bg-warrior-red/5 rounded-full blur-3xl"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 flex items-center justify-center bg-warrior-red/20 rounded-2xl mx-auto mb-6">
            <i className="ri-calendar-event-line text-4xl text-warrior-red"></i>
          </div>
          <h1 className="font-display text-5xl lg:text-6xl text-white mb-4">
            EVENTS &amp; ACTIVITIES
          </h1>
          <p className="font-sans text-lg text-gray-300 max-w-2xl mx-auto">
            Stay tuned for exciting opportunities to learn, grow, and support the Cherokee Warriors community
          </p>
        </div>
      </section>

      {/* Fundraising Events Section */}
      <section className="py-16 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-4 mb-10">
            <div className="w-12 h-12 flex items-center justify-center bg-warrior-red/10 rounded-xl">
              <i className="ri-hand-heart-line text-2xl text-warrior-red"></i>
            </div>
            <div>
              <h2 className="font-display text-4xl text-warrior-black">FUNDRAISING EVENTS</h2>
              <p className="font-sans text-sm text-gray-500 mt-1">Support the Warriors \u2014 every purchase makes a difference</p>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {fundraisingEvents.map((event) => (
              <div key={event.id} className="bg-white rounded-[28px] overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300">
                <div className="w-full overflow-hidden">
                  <img src={event.graphic} alt={event.title} className="w-full h-auto object-contain" />
                </div>
                <div className="p-7">
                  <h3 className="font-display text-2xl text-warrior-black mb-3">{event.title}</h3>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center space-x-2 text-sm text-gray-700">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className="ri-calendar-line text-warrior-red"></i>
                      </div>
                      <span className="font-semibold">{event.date}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-700">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className="ri-time-line text-warrior-red"></i>
                      </div>
                      <span>{event.time}</span>
                    </div>
                    <div className="flex items-start space-x-2 text-sm text-gray-700">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-map-pin-line text-warrior-red"></i>
                      </div>
                      <span>{event.location}</span>
                    </div>
                  </div>
                  <p className="font-sans text-sm text-gray-600 leading-relaxed">{event.details}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cheer Camps Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-4 mb-10">
            <div className="w-12 h-12 flex items-center justify-center bg-warrior-red/10 rounded-xl">
              <i className="ri-star-smile-line text-2xl text-warrior-red"></i>
            </div>
            <div>
              <h2 className="font-display text-4xl text-warrior-black">CAMPS</h2>
              <p className="font-sans text-sm text-gray-500 mt-1">Fun, skill-building camps for young Warriors</p>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {cheerCamps.map((camp) => (
              <div key={camp.id} className="bg-gray-100 rounded-[28px] overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300">
                <div className="w-full overflow-hidden">
                  <img src={camp.graphic} alt={camp.title} className="w-full h-auto object-contain" />
                </div>
                <div className="p-7">
                  <h3 className="font-display text-2xl text-warrior-black mb-3">{camp.title}</h3>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center space-x-2 text-sm text-gray-700">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className="ri-calendar-line text-warrior-red"></i>
                      </div>
                      <span className="font-semibold">{camp.date}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-700">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className="ri-time-line text-warrior-red"></i>
                      </div>
                      <span>{camp.time}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-700">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className="ri-map-pin-line text-warrior-red"></i>
                      </div>
                      <span>{camp.location}</span>
                    </div>
                    {camp.ages && (
                      <div className="flex items-center space-x-2 text-sm text-gray-700">
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                          <i className="ri-user-line text-warrior-red"></i>
                        </div>
                        <span>Ages: {camp.ages}</span>
                      </div>
                    )}
                    <div className="flex items-center space-x-2 text-sm text-gray-700">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className="ri-price-tag-3-line text-warrior-red"></i>
                      </div>
                      <span className="font-semibold text-warrior-red">{camp.cost}</span>
                    </div>
                  </div>
                  <p className="font-sans text-sm text-gray-600 leading-relaxed">{camp.details}</p>
                  {camp.registerLink && (
                    <a
                      href={camp.registerLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-5 inline-block px-8 py-3 bg-warrior-red text-white font-display text-sm rounded-md hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      REGISTER NOW
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Other Events Sections */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          {generalSections.map((section, index) => (
            <div key={section.id} id={section.id} className="bg-gray-100 rounded-[32px] overflow-hidden">
              <div className={`grid lg:grid-cols-2 gap-0 ${index % 2 === 1 ? 'lg:grid-flow-dense' : ''}`}>
                <div className={`relative h-80 lg:h-auto ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                  <div className="absolute inset-0 w-full h-full">
                    <img src={section.image} alt={section.title} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                </div>
                <div className={`p-8 lg:p-12 flex flex-col justify-center ${index % 2 === 1 ? 'lg:col-start-1' : ''}`}>
                  <div className="w-14 h-14 flex items-center justify-center bg-warrior-red/10 rounded-xl mb-6">
                    <i className={`${section.icon} text-3xl text-warrior-red`}></i>
                  </div>
                  <h2 className="font-display text-4xl text-warrior-black mb-4">{section.title}</h2>
                  <p className="font-sans text-base text-gray-600 leading-relaxed mb-6">{section.description}</p>
                  {section.comingSoon && (
                    <div className="inline-flex items-center space-x-3 bg-white px-6 py-3 rounded-full">
                      <div className="w-8 h-8 flex items-center justify-center bg-warrior-red/20 rounded-full">
                        <i className="ri-time-line text-lg text-warrior-red"></i>
                      </div>
                      <div>
                        <p className="font-display text-sm text-warrior-black">COMING SOON</p>
                        <p className="font-sans text-xs text-gray-600">Dates will be announced</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-br from-warrior-red to-red-700 rounded-[32px] p-10 lg:p-14 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-bl-full"></div>
            <div className="absolute bottom-0 left-0 w-40 h-40 bg-black/10 rounded-tr-full"></div>
            <div className="relative z-10">
              <h2 className="font-display text-3xl lg:text-4xl text-white mb-4">STAY IN THE LOOP</h2>
              <p className="font-sans text-base text-white/90 mb-8 max-w-2xl mx-auto">
                Want to be the first to know when events are scheduled? Follow us on social media or reach out to get updates on camps, clinics, and fundraisers.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate('/#contact')}
                  className="px-8 py-3 bg-white text-warrior-red font-display text-base rounded-md hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  CONTACT US
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="px-8 py-3 bg-warrior-black text-white font-display text-base rounded-md hover:bg-gray-900 transition-colors cursor-pointer whitespace-nowrap"
                >
                  BACK TO HOME
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
